import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartData } from 'chart.js';
import {
  addDays,
  addMonths,
  addWeeks,
  differenceInCalendarDays,
  endOfWeek,
  format,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { TimeSeriesPoint } from '../../../../shared/interfaces/analytics.interface';
import { formatCompactCurrency, formatCurrency } from '../../utils/report-format';

type Bucket = 'day' | 'week' | 'month';

interface BucketPoint {
  start: Date;
  label: string;
  longLabel: string;
  value: number;
}

const BUCKET_NOUN: Record<Bucket, string> = { day: 'día', week: 'semana', month: 'mes' };

/**
 * Ingresos cobrados por día, semana o mes según el largo del rango.
 * Rellena con cero los períodos sin pagos para que el eje de tiempo sea continuo.
 */
@Component({
  selector: 'app-revenue-chart',
  standalone: true,
  imports: [BaseChartDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (total === 0) {
      <div class="flex h-64 flex-col items-center justify-center text-center">
        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">No hay pagos registrados en estas fechas</p>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Los pagos aparecen aquí cuando se registran en Caja o por link de pago.</p>
      </div>
    } @else {
      <p class="mb-4 text-sm text-gray-600 dark:text-gray-300">
        Promedio de <span class="font-semibold text-gray-900 tabular-nums dark:text-white">{{ average }}</span> por {{ bucketNoun }}.
        Mejor {{ bucketNoun }}: <span class="font-semibold text-gray-900 dark:text-white">{{ best?.longLabel }}</span>
        con <span class="font-semibold text-gray-900 tabular-nums dark:text-white">{{ bestValue }}</span>.
      </p>
      <div class="relative h-64">
        <canvas baseChart [data]="chartData" [options]="chartOptions" [type]="'bar'"
          role="img" [attr.aria-label]="'Ingresos por ' + bucketNoun + '. Total ' + totalLabel"></canvas>
      </div>
      <table class="sr-only">
        <caption>Ingresos por {{ bucketNoun }}</caption>
        <tbody>
          @for (p of points; track p.start.getTime()) {
            <tr><th scope="row">{{ p.longLabel }}</th><td>{{ fmt(p.value) }}</td></tr>
          }
        </tbody>
      </table>
    }
  `,
})
export class RevenueChart implements OnChanges {
  @Input() data: TimeSeriesPoint[] = [];
  @Input({ required: true }) rangeStart!: Date;
  @Input({ required: true }) rangeEnd!: Date;

  points: BucketPoint[] = [];
  total = 0;
  totalLabel = '';
  average = '';
  best: BucketPoint | null = null;
  bestValue = '';
  bucketNoun = 'día';
  readonly fmt = formatCurrency;

  chartData: ChartData<'bar'> = { labels: [], datasets: [] };

  readonly chartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 300 },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#101828',
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: (items) => this.points[items[0]?.dataIndex]?.longLabel ?? '',
          label: (ctx) => formatCurrency(ctx.parsed.y ?? 0),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: '#667085', font: { size: 11 }, maxRotation: 0, autoSkipPadding: 12 },
      },
      y: {
        beginAtZero: true,
        border: { display: false },
        grid: { color: 'rgba(152,162,179,0.18)' },
        ticks: {
          color: '#667085',
          font: { size: 11 },
          padding: 6,
          maxTicksLimit: 5,
          callback: (value) => (typeof value === 'number' ? formatCompactCurrency(value) : value),
        },
      },
    },
  };

  ngOnChanges(): void {
    if (!this.rangeStart || !this.rangeEnd) return;
    const days = differenceInCalendarDays(this.rangeEnd, this.rangeStart) + 1;
    const bucket: Bucket = days <= 45 ? 'day' : days <= 180 ? 'week' : 'month';
    this.bucketNoun = BUCKET_NOUN[bucket];
    this.points = this.buildBuckets(bucket);

    this.total = this.points.reduce((s, p) => s + p.value, 0);
    this.totalLabel = formatCurrency(this.total);
    this.average = formatCurrency(this.points.length ? this.total / this.points.length : 0);
    this.best = this.points.reduce<BucketPoint | null>((a, b) => (!a || b.value > a.value ? b : a), null);
    this.bestValue = formatCurrency(this.best?.value ?? 0);

    this.chartData = {
      labels: this.points.map((p) => p.label),
      datasets: [
        {
          data: this.points.map((p) => p.value),
          backgroundColor: '#465fff',
          hoverBackgroundColor: '#3641f5',
          borderRadius: { topLeft: 4, topRight: 4 },
          borderSkipped: 'bottom',
          maxBarThickness: 36,
          categoryPercentage: 0.8,
          barPercentage: 0.9,
        },
      ],
    };
  }

  private buildBuckets(bucket: Bucket): BucketPoint[] {
    const keyOf = (d: Date) => {
      const start = bucket === 'day' ? startOfDay(d) : bucket === 'week' ? startOfWeek(d, { weekStartsOn: 1 }) : startOfMonth(d);
      return start.getTime();
    };

    const sums = new Map<number, number>();
    for (const p of this.data) {
      // La fecha viene como día local sin zona: parseISO la deja en hora local
      const key = keyOf(parseISO(p.date));
      sums.set(key, (sums.get(key) ?? 0) + Number(p.value));
    }

    const result: BucketPoint[] = [];
    let cursor = new Date(keyOf(this.rangeStart));
    const end = this.rangeEnd.getTime();
    while (cursor.getTime() <= end) {
      result.push({ start: cursor, value: sums.get(cursor.getTime()) ?? 0, ...this.labels(cursor, bucket) });
      cursor = bucket === 'day' ? addDays(cursor, 1) : bucket === 'week' ? addWeeks(cursor, 1) : addMonths(cursor, 1);
    }
    return result;
  }

  private labels(d: Date, bucket: Bucket): { label: string; longLabel: string } {
    if (bucket === 'day') {
      return { label: format(d, 'd MMM', { locale: es }), longLabel: format(d, "EEEE d 'de' MMMM", { locale: es }) };
    }
    if (bucket === 'week') {
      const end = endOfWeek(d, { weekStartsOn: 1 });
      return {
        label: format(d, 'd MMM', { locale: es }),
        longLabel: `semana del ${format(d, "d 'de' MMM", { locale: es })} al ${format(end, "d 'de' MMM", { locale: es })}`,
      };
    }
    return { label: format(d, 'MMM', { locale: es }), longLabel: format(d, "MMMM 'de' yyyy", { locale: es }) };
  }
}
